import React, { useState } from "react";
import { makeStyles, Popover, Typography, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import GridItem from "../Grid/GridItem";
import {
  blackColor,
  grayColor,
  hexToRgb,
} from "../../assets/jss/material-dashboard-pro-react";
import { getScenarioStatusIcon } from "../../views/Cockpit/viewServices/constants/statusConstants";
import { useScenarioDataContext } from "../../services/context/ScenarioDataContext";

// Helper function to calculate and format duration
const formatDuration = (startTimestamp, endTimestamp) => {
  if (!startTimestamp || !endTimestamp) {
    return " - ";
  }

  const start = new Date(startTimestamp);
  const end = new Date(endTimestamp);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 0) {
    return " - ";
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  const remainingSeconds = diffSeconds % 60;
  const remainingMinutes = diffMinutes % 60;
  const remainingHours = diffHours % 24;

  let parts = [];

  if (diffDays > 0) {
    parts.push(`${diffDays}d`);
  }
  if (remainingHours > 0) {
    parts.push(`${remainingHours.toString().padStart(2, "0")}h`);
  }
  parts.push(`${remainingMinutes.toString().padStart(2, "0")}m`);
  parts.push(`${remainingSeconds.toString().padStart(2, "0")}s`);

  return parts.join(" ");
};

const useStyles = makeStyles((theme) => ({
  actionWrapperOuter: {
    paddingLeft: "10px !important",
    paddingRight: "10px !important",
  },
  actionWrapper: {
    cursor: "pointer",
    opacity: "0.8",
    transition: "opacity 0.1s",
    "&:hover,&:active": {
      opacity: "1",
      "& div:first-child": {
        boxShadow: "0 0px 30px -10px rgba(" + hexToRgb(blackColor) + ", 0.3)",
      },
      "& div:first-child > svg": {
        transform: "scale(1.2)",
      },
    },
    "&:active": {
      "& div:first-child": {
        boxShadow:
          "0 0px 30px -10px rgba(" + hexToRgb(blackColor) + ", 0.7) !important",
      },
    },
  },
  actionIcon: {
    color: grayColor[6],
    margin: "6px auto 0",
    width: "42px",
    height: "42px",
    border: "1px solid " + grayColor[11],
    borderRadius: "50%",
    textAlign: "center",
    lineHeight: "58px",
    transition: "box-shadow 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      transition: "transform 0.2s",
      width: "26px",
      height: "26px",
    },
    "& .fab,& .fas,& .far,& .fal,& .material-icons": {
      width: "26px",
      fontSize: "26px",
    },
  },
  actionText: {
    color: grayColor[0],
    marginTop: "6px",
    marginBottom: "2px",
    textAlign: "center",
    fontSize: "10px",
  },
  popoverPaper: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    width: 300,
  },
}));

const FloatingStatus = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.floatingStatus",
  });
  const { t: tActionBar } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.actionbar",
  });
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  // Use the scenario data context
  const { latestScenario } = useScenarioDataContext();

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getStatusIcon = () => {
    if (!latestScenario) {
      return getScenarioStatusIcon("DEFAULT", 26);
    }
    return getScenarioStatusIcon(latestScenario.status, 26);
  };

  return (
    <>
      <GridItem
        className={classes.actionWrapperOuter}
        onClick={handleOpenPopover}
      >
        <div className={classes.actionWrapper}>
          <Tooltip
            title={
              latestScenario
                ? t("status", { status: latestScenario.status })
                : t("no_scenarios_found")
            }
          >
            <div className={classes.actionIcon}>{getStatusIcon()}</div>
          </Tooltip>
          <h6 className={classes.actionText}>{tActionBar("mostRecent")}</h6>
        </div>
      </GridItem>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
        transitionDuration={300}
      >
        {latestScenario ? (
          <>
            <Typography>
              <strong>{t("scenario_name")}:</strong>{" "}
              {latestScenario.scenario_name}
            </Typography>
            <Typography>
              <strong>{t("status_label")}:</strong> {latestScenario.status}
            </Typography>
            <Typography>
              <strong>{t("created_by")}:</strong>{" "}
              {latestScenario.created_by_user || " - "}
            </Typography>
            <Typography>
              <strong>{t("duration")}:</strong>{" "}
              {formatDuration(
                latestScenario.start_timestamp,
                latestScenario.end_timestamp,
              )}
            </Typography>
          </>
        ) : (
          <Typography>{t("no_scenario_data")}</Typography>
        )}
      </Popover>
    </>
  );
};

export default FloatingStatus;
